import { cn } from '@/lib/utils';

// One bucket of uptime data. `key` is a UTC-aligned label matching how the
// server groups: 'YYYY-MM-DD' for daily, 'YYYY-MM-DDTHH:00:00Z' for hourly.
interface Bucket {
  key: string;
  checks_total: number;
  checks_up: number;
}

interface UptimeBarProps {
  buckets: Bucket[];
  /** Granularity of each bar. */
  interval: 'day' | 'hour';
  /** How many bars to render (trailing from "now"). */
  count: number;
}

// Build the list of expected keys for [now - (count-1)*step .. now], filling
// gaps with null so the bar has a fixed number of slots.
function expandKeys(
  interval: 'day' | 'hour',
  count: number,
  byKey: Map<string, Bucket>
) {
  const out: Array<{ key: string; pct: number | null; label: string }> = [];
  const now = new Date();

  for (let i = count - 1; i >= 0; i--) {
    let key: string;
    let label: string;

    if (interval === 'day') {
      const d = new Date(now);
      d.setUTCDate(d.getUTCDate() - i);
      key = d.toISOString().slice(0, 10); // YYYY-MM-DD
      label = key;
    } else {
      const d = new Date(now);
      d.setUTCMinutes(0, 0, 0);
      d.setUTCHours(d.getUTCHours() - i);
      key = d.toISOString().slice(0, 13) + ':00:00Z'; // YYYY-MM-DDTHH:00:00Z
      label = d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    const row = byKey.get(key);
    if (row && row.checks_total > 0) {
      out.push({ key, pct: (row.checks_up / row.checks_total) * 100, label });
    } else {
      out.push({ key, pct: null, label });
    }
  }
  return out;
}

export function UptimeBar({ buckets, interval, count }: UptimeBarProps) {
  const byKey = new Map(buckets.map((b) => [b.key, b]));
  const bars = expandKeys(interval, count, byKey);

  const oldestLabel =
    interval === 'hour' ? `${count}h ago` : `${count}d ago`;

  return (
    <div className="w-full">
      <div className="flex items-end gap-[2px] h-10 w-full">
        {bars.map((b) => {
          const color =
            b.pct === null
              ? 'bg-zinc-300 dark:bg-zinc-700'
              : b.pct >= 99.5
                ? 'bg-green-500'
                : b.pct >= 95
                  ? 'bg-yellow-500'
                  : 'bg-red-500';
          const title =
            b.pct === null
              ? `${b.label}: no data`
              : `${b.label}: ${b.pct.toFixed(2)}% uptime`;
          return (
            <div
              key={b.key}
              title={title}
              className={cn('h-full flex-1 min-w-[3px] rounded-sm', color)}
            />
          );
        })}
      </div>
      <div className="flex justify-between mt-1 text-[10px] text-muted-foreground tabular-nums">
        <span>{oldestLabel}</span>
        <span>now</span>
      </div>
    </div>
  );
}
